<script setup lang="ts">
import type { IResponseData } from '~/types'
import { FeaturePagination } from '~/components/feature/pagination'
import { LucideNewspaper } from 'lucide-vue-next'
import type { ICard, IProps } from '~/components/entity/card/item'
import { EntityCardItem, EntityCardSkeleton } from '~/components/entity/card'
import { useFetchLocale } from '~/composables/useFetchLocale'

const props = withDefaults(defineProps<IProps>(), {
  hideOrganizationDetails: false,
  hidePagination: false
})

const emit = defineEmits<{
  loaded: [hasData: boolean]
}>()

const config = useRuntimeConfig()
const baseApiUrl = config.public.baseApiUrl

const route = useRoute()

const page = ref(Number(route.query.page) || 1)

const query = computed(() =>
  new URLSearchParams({
    ...Object.fromEntries(Object.entries(props.queryParams || {}).map(([k, v]) => [k, String(v)])),
    page: String(page.value)
  }).toString()
)

const url = computed(() => `${baseApiUrl}/card?${query.value}`)

const {
  data: cards,
  status
} = useFetchLocale<IResponseData<ICard[]>>(url, {
  key: computed(() => `cards-${page.value}-${query.value}`)
})

watchEffect(() => {
  if (status.value === 'success') {
    emit('loaded', !!cards.value?.data?.length)
  }
})

const mappedCards = computed(() =>
  cards.value?.data?.map((item) => ({
    ...item,
    ratings: item.ratings?.average_rate ?? null
  })) ?? []
)

const fetchCards = (pageNumber: number) => {
  page.value = pageNumber
}
</script>

<template>
  <section v-if="status === 'success'">
    <div v-if="mappedCards.length" class="CardsList">
      <template v-for="item in mappedCards" :key="item.id">
        <EntityCardItem
          :item="item"
          :hide-organization-details="props.hideOrganizationDetails"
          :position="props.position"
        />
      </template>
      <div v-if="!props.hidePagination && cards.meta.last_page !== 1" class="mt-7 flex justify-end">
        <FeaturePagination :meta="cards.meta" :links="cards.links" @change="fetchCards" />
      </div>
    </div>
    <Alert v-else variant="blue">
      <LucideNewspaper />
      <AlertDescription>{{ $t('widget.cards.list.no_cards') }}</AlertDescription>
    </Alert>
  </section>

  <section v-else>
    <EntityCardSkeleton
      :count="Number(props.queryParams?.per_page || 3)"
      :hide-organization-details="props.hideOrganizationDetails"
    />
  </section>
</template>
